import { inject, InjectionToken } from '@angular/core';
import {
  type ActivatedRouteSnapshot,
  type CanDeactivateFn,
  type Params,
  type RouterStateSnapshot,
} from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import {
  RouterStateSerializer as _RouterStateSerializer,
  ROUTER_NAVIGATED,
} from '@ngrx/router-store';
import { Store, type ActionType } from '@ngrx/store';
import { castArray } from 'lodash-es';

import { takeFirst } from '../../helpers/observable.helpers';

import { type BaseContextData, type BaseContextIds } from './auth.service';

export interface BaseRouterStoreState<
  ModulesName extends string = string,
  ContextIds extends BaseContextIds = BaseContextIds,
> extends BaseContextData<ModulesName, ContextIds> {
  url: string;
}

export const CONTEXT_IDS = new InjectionToken(
  'RouterStateSerializerContextIds',
);

export const STRINGS_PARAM_KEYS = new InjectionToken<string[]>(
  'RouterStateSerializerStringsParamKeys',
);

export class RouterStateSerializer<
  ModulesNames extends string = string,
  ContextIds extends BaseContextIds = BaseContextIds,
> extends _RouterStateSerializer<
  BaseRouterStoreState<ModulesNames, ContextIds>
> {
  private readonly _contextIds = inject<ContextIds>(CONTEXT_IDS);
  private readonly _stringsParamKeys =
    inject(STRINGS_PARAM_KEYS, {
      optional: true,
    }) || [];

  public serialize(
    state: RouterStateSnapshot,
    maxRoute?: ActivatedRouteSnapshot,
  ): BaseRouterStoreState<ModulesNames, ContextIds> {
    let route = state.root;

    const url = state.url;
    const params: ContextIds = { ...this._contextIds };
    const queryParams: Params = {};
    const moduleNames: ModulesNames[] = [];
    let data: Record<string, any> = {};

    while (route?.firstChild && route !== maxRoute) {
      route = route.firstChild;

      if (
        route.data.moduleName &&
        !moduleNames.includes(route.data.moduleName)
      ) {
        moduleNames.push(route.data.moduleName);
      }

      for (const id of Object.keys(route.params)) {
        if (this._stringsParamKeys.includes(id)) {
          (params as any)[id] = route.params[id];
        } else if (!isNaN(+route.params[id])) {
          (params as any)[id] = +route.params[id];
        }
      }

      for (const id in route.queryParams) {
        if (route.queryParams.hasOwnProperty(id)) {
          if (isNaN(+route.queryParams[id])) {
            queryParams[id] = route.queryParams[id];
          } else {
            queryParams[id] = +route.queryParams[id];
          }
        }
      }

      data = {
        ...data,
        ...route.data,
      };
    }

    // Only return an object including the URL, params, query params and data
    // instead of the entire snapshot
    return { url, params, queryParams, data, moduleNames };
  }
}

export function dispatchOnNavigatedFactory(
  actions: ActionType<any> | ActionType<any>[],
): () => void {
  const store$ = inject(Store);
  const actions$ = inject(Actions);

  return () => {
    actions$.pipe(ofType(ROUTER_NAVIGATED), takeFirst()).subscribe(_ => {
      for (const a of castArray(actions)) {
        store$.dispatch(a);
      }
    });
  };
}

export interface DispatchGuardConfig {
  dispatchOnNavigated?: (data: BaseRouterStoreState) => ActionType<any>[];
  dispatchOnSuccess?: (data: BaseRouterStoreState) => ActionType<any>[];
}

export function dispatcherFactory(
  config: DispatchGuardConfig,
  state: RouterStateSnapshot,
): () => void {
  const store$ = inject(Store);
  const routerStateSerializer = inject(RouterStateSerializer);
  const data = routerStateSerializer.serialize(state);

  const dispatchOnNavigated =
    config.dispatchOnNavigated &&
    dispatchOnNavigatedFactory(config.dispatchOnNavigated(data));

  return () => {
    if (config.dispatchOnSuccess) {
      const actions = config.dispatchOnSuccess(data);

      for (const a of actions) {
        store$.dispatch(a);
      }
    }

    if (dispatchOnNavigated) {
      dispatchOnNavigated();
    }
  };
}

export function dispatchGuard(config: DispatchGuardConfig) {
  return (_: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean => {
    dispatcherFactory(config, state)();

    return true;
  };
}

export function leaveDispatchGuard(
  config: DispatchGuardConfig,
): CanDeactivateFn<any> {
  return (
    _: any,
    __: ActivatedRouteSnapshot,
    ___: RouterStateSnapshot,
    nextState: RouterStateSnapshot,
  ): boolean => {
    dispatcherFactory(config, nextState)();

    return true;
  };
}
